import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import styles from "./buyerCompletionList.module.scss";

interface BuyerCompletionListProps {
  title?: string;
  data?: any[];
  nameKey?: string;
  rateKey?: string;
  countKey?: string;
  completedCountKey?: string;
  countLabel?: string;
  showThisYear?: boolean;
}

const BuyerCompletionList: React.FC<BuyerCompletionListProps> = ({
  title = "% Completion By Buyer",
  data = [],
  nameKey = "name",
  rateKey = "completionRate",
  countKey = "contracts",
  completedCountKey = "completedCount",
  countLabel = "contracts",
  showThisYear = false,
}) => {
  const defaultColors = ["linear-gradient(180deg, #08ADC2 0%, #455391 100%)"];

  // Generate initials from name
  const getInitials = (name: string): string => {
    if (!name || typeof name !== "string") return "NA";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Transform and normalize data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => ({
      name: item[nameKey] || item.name || `User ${index + 1}`,
      initials:
        item.initials ||
        item.avatar ||
        getInitials(item[nameKey] || item.name || ""),
      contracts: Number(
        item[countKey] || item.contractsCount || item.contracts || 0,
      ),
      completedCount: Number(
        item[completedCountKey] || item.completedCount || 0,
      ),
      completionRate: Number(
        item[rateKey] || item.completionRate || item.rate || 0,
      ),
      color: item.color || defaultColors[0],
    }));
  }, [data, nameKey, rateKey, countKey, completedCountKey, defaultColors]);

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#08ADC2",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>
        <Box
          className={styles.listContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
        {showThisYear && (
          <Chip
            label="This Year"
            size="small"
            sx={{
              backgroundColor: "#e6faf5",
              color: "#08ADC2",
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
        )}
      </Box>

      <Box className={styles.listContainer}>
        {chartData.map((buyer, index) => (
          <Box key={index} className={styles.listItem}>
            <Box className={styles.buyerInfo}>
              <div className={styles.buyerDetails}>
                <Box
                  className={styles.avatar}
                  style={{ backgroundColor: buyer.color }}
                >
                  <Typography className={styles.initials}>
                    {buyer.initials}
                  </Typography>
                </Box>
                <Box>
                  <Typography className={styles.name}>
                    {buyer.name} - {buyer.contracts} {countLabel}
                  </Typography>
                  {/* <Typography className={styles.contracts}>
                      {buyer.contracts} {countLabel}
                    </Typography> */}
                  <Typography className={styles.contracts}>
                    Completed: {buyer.completedCount}
                  </Typography>
                </Box>
              </div>
              <Box className={styles.nameSection}>
                <Typography className={styles.percentage}>
                  {buyer.completionRate}%
                </Typography>
              </Box>
            </Box>
            <Box className={styles.progressSection}>
              <Box className={styles.progressBarContainer}>
                <Box
                  className={styles.progressBarFill}
                  style={{
                    width: `${Math.min(buyer.completionRate, 100)}%`,
                    background: defaultColors[0],
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BuyerCompletionList;
